import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  Activity,
  Archive,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { API } from "../../App";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendente", icon: Clock, color: "text-amber-300", border: "border-amber-500/40", bg: "bg-amber-500/10" },
  { value: "in_progress", label: "Em desenvolvimento", icon: Activity, color: "text-cyan-300", border: "border-cyan-500/40", bg: "bg-cyan-500/10" },
  { value: "completed", label: "Concluído", icon: CheckCircle, color: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/10" },
  { value: "cancelled", label: "Cancelado", icon: Archive, color: "text-slate-400", border: "border-slate-600", bg: "bg-slate-800/50" },
];

const PRIORITY_OPTIONS = [
  { value: "high", label: "Alta", color: "text-red-300", border: "border-red-500/40" },
  { value: "normal", label: "Normal", color: "text-slate-300", border: "border-slate-600" },
  { value: "low", label: "Baixa", color: "text-slate-500", border: "border-slate-700" },
];

function statusMeta(status) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

function priorityMeta(p) {
  return PRIORITY_OPTIONS.find((x) => x.value === p) || PRIORITY_OPTIONS[1];
}

export default function RoadmapPanel() {
  const [view, setView] = useState("active"); // active | done
  const [items, setItems] = useState([]);
  const [moduleStats, setModuleStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const status = view === "active" ? "pending,in_progress" : "completed,cancelled";
      const [list, modules] = await Promise.all([
        axios.get(`${API}/super-admin/todos`, {
          params: { status },
          withCredentials: true,
        }),
        axios.get(`${API}/super-admin/todos/modules`, { withCredentials: true }),
      ]);
      setItems(list.data || []);
      setModuleStats(modules.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar roadmap");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const toggleCollapse = (mod) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return next;
    });
  };

  const updateStatus = async (item, newStatus) => {
    try {
      await axios.put(
        `${API}/super-admin/todos/${item.id}`,
        { status: newStatus },
        { withCredentials: true },
      );
      toast.success(`Estado: ${statusMeta(newStatus).label}`);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Eliminar "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/super-admin/todos/${item.id}`, { withCredentials: true });
      toast.success("Item eliminado");
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao eliminar");
    }
  };

  // Group by module
  const grouped = items.reduce((acc, it) => {
    const key = it.module || "Geral";
    (acc[key] ||= []).push(it);
    return acc;
  }, {});
  const moduleNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b, "pt"));

  const totalActive = moduleStats.reduce((acc, m) => acc + m.pending + m.in_progress, 0);
  const totalDone = moduleStats.reduce((acc, m) => acc + m.completed, 0);

  return (
    <div className="space-y-4" data-testid="roadmap-panel">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setView("active")}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
              view === "active"
                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
            }`}
            data-testid="roadmap-view-active"
          >
            Ativas ({totalActive})
          </button>
          <button
            type="button"
            onClick={() => setView("done")}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
              view === "done"
                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
            }`}
            data-testid="roadmap-view-done"
          >
            Funcionalidades implementadas ({totalDone})
          </button>
        </div>
        <Button
          size="sm"
          className="btn-cockpit"
          onClick={() => setCreating(true)}
          data-testid="roadmap-add-item"
        >
          <Plus className="w-4 h-4 mr-1" />
          Nova ideia
        </Button>
      </div>

      {/* Module overview chips */}
      {moduleStats.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {moduleStats.map((m) => (
            <span
              key={m.module}
              className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-900 border border-slate-800 text-slate-400"
            >
              <span className="text-slate-300">{m.module}</span> · {m.pending + m.in_progress} ativas / {m.completed} done
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500 text-sm">A carregar...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm" data-testid="roadmap-empty">
          {view === "active" ? "Sem itens ativos." : "Ainda não há funcionalidades implementadas."}
        </div>
      ) : (
        <div className="space-y-3">
          {moduleNames.map((mod) => {
            const isOpen = !collapsed.has(mod);
            return (
              <Card key={mod} className="instrument-panel">
                <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleCollapse(mod)}>
                  <CardTitle className="font-chivo text-sm flex items-center gap-2">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <span className="text-orange-400">{mod}</span>
                    <span className="text-[11px] text-slate-500 font-normal">
                      ({grouped[mod].length} {grouped[mod].length === 1 ? "item" : "itens"})
                    </span>
                  </CardTitle>
                </CardHeader>
                {isOpen && (
                  <CardContent className="space-y-2 pt-0">
                    {grouped[mod].map((it) => (
                      <RoadmapRow
                        key={it.id}
                        item={it}
                        onEdit={() => setEditing(it)}
                        onStatusChange={(s) => updateStatus(it, s)}
                        onDelete={() => removeItem(it)}
                      />
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <RoadmapEditor
          item={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            fetchItems();
          }}
        />
      )}
    </div>
  );
}

function RoadmapRow({ item, onEdit, onStatusChange, onDelete }) {
  const sm = statusMeta(item.status);
  const Pm = priorityMeta(item.priority);
  const StIcon = sm.icon;

  return (
    <div
      className={`flex items-start gap-3 px-3 py-2.5 rounded border ${sm.border} ${sm.bg}`}
      data-testid={`roadmap-item-${item.id}`}
    >
      <StIcon className={`w-4 h-4 mt-0.5 shrink-0 ${sm.color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-medium text-cockpit-text-bright">{item.title}</h4>
          <span
            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${Pm.color} border ${Pm.border}`}
          >
            {Pm.label}
          </span>
        </div>
        {item.description && (
          <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap leading-relaxed">
            {item.description}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Select value={item.status} onValueChange={onStatusChange}>
          <SelectTrigger className="h-7 w-36 bg-slate-900 border-slate-700 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-slate-500 hover:text-cyan-300"
          onClick={onEdit}
          title="Editar"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-slate-500 hover:text-red-400"
          onClick={onDelete}
          title="Eliminar"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function RoadmapEditor({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState({
    title: item?.title || "",
    description: item?.description || "",
    module: item?.module || "",
    priority: item?.priority || "normal",
    status: item?.status || "pending",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Título obrigatório");
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        await axios.post(`${API}/super-admin/todos`, form, { withCredentials: true });
        toast.success("Item criado");
      } else {
        await axios.put(`${API}/super-admin/todos/${item.id}`, form, { withCredentials: true });
        toast.success("Item atualizado");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-orange-400" />
            {isNew ? "Nova ideia" : "Editar item"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400">Título *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Adicionar exportação de exames em CSV"
              className="bg-slate-900 border-slate-700"
              data-testid="roadmap-input-title"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400">Descrição</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Detalhes, contexto, sub-tarefas..."
              className="bg-slate-900 border-slate-700 min-h-[100px]"
              data-testid="roadmap-input-description"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-slate-400">Módulo</label>
              <Input
                value={form.module}
                onChange={(e) => setForm({ ...form, module: e.target.value })}
                placeholder="Ex: Direção de Instrução"
                className="bg-slate-900 border-slate-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Prioridade</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="btn-secondary-cockpit">
            Cancelar
          </Button>
          <Button onClick={submit} disabled={busy} className="btn-cockpit" data-testid="roadmap-save">
            {busy ? "A guardar..." : isNew ? "Criar" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
